/**
 * The app state machine. Hand-written and explicit — no library, no implicit transitions.
 *
 * Two rules here exist because the first plan review found them missing, and both are the kind of
 * bug that only shows up on a real device:
 *
 *  1. `PERMISSION` is SKIPPED when an AR session is already running. Re-probing getUserMedia while
 *     MindAR holds the stream can black out the feed on iOS — which would break round 2 only, in a
 *     way no camera-less debug traversal can ever reproduce.
 *
 *  2. `targetLost` is NOT an event here. It never rewinds state; it only toggles an overlay whose
 *     visibility is scoped per state (see `showsLostOverlay`).
 */

export const STATES = [
  'BOOT',
  'UNSUPPORTED',
  'PERMISSION',
  'SCANNING',
  'INTRO',
  'JOIN_GATE',
  'PLAY',
  'RESOLVING',
  'REVEAL',
  'RESULT',
  'DONE',
] as const;

export type AppState = (typeof STATES)[number];

export type AppEvent =
  | 'START' // BOOT: user tapped START
  | 'UNSUPPORTED_DETECTED' // no WebGL / in-app browser / insecure origin / no camera
  | 'PERMISSION_GRANTED'
  | 'PERMISSION_DENIED'
  | 'TARGET_READY' // target found AND the GLB has finished loading
  | 'INTRO_END' // the Intro clip reported `finished`
  | 'JOIN' // user tapped 시작하기
  | 'COIN_HIT' // accepted coin tap (already disarmed by the caller)
  | 'DRAW_OK'
  | 'DRAW_FAIL'
  | 'RETRY'
  | 'CAN_STANDING' // Signal_CanStanding latched
  | 'COUPON_SAVED' // user confirmed / share resolved
  | 'RESTART' // tap on DONE
  | 'IDLE_TIMEOUT';

/** Explicit table. An event absent from a state's row is ignored, never a silent fallthrough. */
const TRANSITIONS: Readonly<Record<AppState, Partial<Record<AppEvent, AppState>>>> = {
  BOOT: { START: 'PERMISSION', UNSUPPORTED_DETECTED: 'UNSUPPORTED' },
  UNSUPPORTED: { RETRY: 'BOOT' },
  PERMISSION: { PERMISSION_GRANTED: 'SCANNING', PERMISSION_DENIED: 'BOOT', UNSUPPORTED_DETECTED: 'UNSUPPORTED' },
  SCANNING: { TARGET_READY: 'INTRO', IDLE_TIMEOUT: 'BOOT' },
  INTRO: { INTRO_END: 'JOIN_GATE', IDLE_TIMEOUT: 'BOOT' },
  JOIN_GATE: { JOIN: 'PLAY', IDLE_TIMEOUT: 'BOOT' },
  PLAY: { COIN_HIT: 'RESOLVING', IDLE_TIMEOUT: 'BOOT' },
  RESOLVING: { DRAW_OK: 'REVEAL', DRAW_FAIL: 'PLAY', IDLE_TIMEOUT: 'BOOT' },
  REVEAL: { CAN_STANDING: 'RESULT', IDLE_TIMEOUT: 'BOOT' },
  RESULT: { COUPON_SAVED: 'DONE', IDLE_TIMEOUT: 'BOOT' },
  DONE: { RESTART: 'BOOT', IDLE_TIMEOUT: 'BOOT' },
};

/**
 * States where losing the target should surface the "re-aim" overlay.
 * RESULT and DONE deliberately do not: the coupon sheet is up, AR is irrelevant, and telling the
 * user to re-aim at a panel over their coupon is nonsense.
 */
const LOST_OVERLAY_STATES: ReadonlySet<AppState> = new Set<AppState>(['INTRO', 'JOIN_GATE', 'PLAY', 'RESOLVING', 'REVEAL']);

/**
 * States where losing the target must also disable the on-screen affordance.
 * Without this a user who lowers the phone during INTRO reaches PLAY with an invisible machine and
 * every tap silently rejected by the `anchor.group.visible` guard — a dead end with no feedback.
 */
const SUPPRESS_AFFORDANCE_STATES: ReadonlySet<AppState> = new Set<AppState>(['JOIN_GATE', 'PLAY']);

export function showsLostOverlay(state: AppState): boolean {
  return LOST_OVERLAY_STATES.has(state);
}

export function suppressesAffordance(state: AppState): boolean {
  return SUPPRESS_AFFORDANCE_STATES.has(state);
}

/** Whether an AR session is live in this state — used to decide if PERMISSION can be skipped. */
export function hasLiveSession(state: AppState): boolean {
  return state !== 'BOOT' && state !== 'UNSUPPORTED' && state !== 'PERMISSION';
}

export interface Transition {
  readonly from: AppState;
  readonly to: AppState;
  readonly event: AppEvent;
}

export type TransitionListener = (t: Transition) => void;

export interface MachineOptions {
  /**
   * True once MindAR has been started. When set, `START` goes straight to SCANNING: opening a
   * second camera stream while the session holds one can kill the feed on iOS.
   */
  isSessionRunning?: () => boolean;
}

export class StateMachine {
  private current: AppState = 'BOOT';
  private readonly listeners = new Set<TransitionListener>();
  private readonly log: Transition[] = [];

  constructor(private readonly options: MachineOptions = {}) {}

  get state(): AppState {
    return this.current;
  }

  /** Full transition history — surfaced in the debug HUD and used by the playwright flow check. */
  get history(): readonly Transition[] {
    return this.log;
  }

  onTransition(fn: TransitionListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** @returns true if the event caused a transition. Unknown events are ignored, never thrown. */
  send(event: AppEvent): boolean {
    const to = this.resolve(this.current, event);
    if (to === undefined || to === this.current) return false;

    const transition: Transition = { from: this.current, to, event };
    this.current = to;
    this.log.push(transition);
    for (const fn of this.listeners) fn(transition);
    return true;
  }

  can(event: AppEvent): boolean {
    return this.resolve(this.current, event) !== undefined;
  }

  private resolve(from: AppState, event: AppEvent): AppState | undefined {
    // Rule 1: skip the permission screen when a session is already live.
    if (from === 'BOOT' && event === 'START' && this.options.isSessionRunning?.() === true) {
      return 'SCANNING';
    }
    return TRANSITIONS[from][event];
  }

  /** Debug / test helper. Bypasses the table deliberately — never call this from product code. */
  forceState(state: AppState): void {
    if (state === this.current) return;
    const transition: Transition = { from: this.current, to: state, event: 'RETRY' };
    this.current = state;
    this.log.push(transition);
    for (const fn of this.listeners) fn(transition);
  }
}
